import type { Apartment } from '../api.apartments';

/**
 * What a guest's money is for. Each purpose can use a different connected Stripe account on the
 * same apartment (issue #114). Mirrors the backend's `PaymentPurpose`.
 */
export type PaymentPurpose = 'CITY_TAX' | 'UPSELL' | 'DEPOSIT';

const PURPOSES: PaymentPurpose[] = ['CITY_TAX', 'UPSELL', 'DEPOSIT'];

/** `{ id: 0 }` is how the backend is told "no account for this purpose". */
const ref = (id?: number) => ({ id: id && id > 0 ? id : 0 });

/**
 * Applies the three account pickers to a save payload.
 *
 * All three objects are sent on every save, even when one is empty. That is not redundancy: the
 * backend treats a body that carries the upsell or deposit object as the new per-purpose form and
 * stores exactly what it says (an empty one meaning "none"), while a body without them is an older
 * cached frontend whose save must leave the two new accounts alone. Leaving an empty picker out
 * would therefore turn "I cleared it" into "I did not touch it".
 *
 * `paymentMethod` stays the city tax account, which is what that field has always meant.
 */
export function withPaymentMethodPayload<T extends Apartment>(form: T): T {
  return {
    ...form,
    paymentMethod: ref(form.paymentMethodId),
    upsellPaymentMethod: ref(form.upsellPaymentMethodId),
    depositPaymentMethod: ref(form.depositPaymentMethodId),
  };
}

/**
 * The purpose the backend refused, when a save failed with 400 INVALID_PAYMENT_METHOD (the chosen
 * account is not one of this manager's Stripe accounts); otherwise null.
 */
export function invalidPaymentMethodPurpose(err: unknown): PaymentPurpose | null {
  const data = (err as { response?: { data?: { error?: unknown; purpose?: unknown } } })?.response?.data;
  if (!data || data.error !== 'INVALID_PAYMENT_METHOD') return null;
  return PURPOSES.find(p => p === data.purpose) ?? null;
}
